import { useEffect, useRef } from 'react'
import { chatService } from '@/services'
import { ChatAction, Conversation } from '../../state/types'

interface BootstrapParams {
  conversationId?: string
}

interface NavigationState {
  initialMessage?: string
  tags?: Array<{ id: string; target_username: string }>
  status?: 'initialized' | 'notInitialized'
  conversation?: Conversation
}

export function useConversationBootstrap(
  params: BootstrapParams,
  navState: NavigationState | null,
  dispatch: React.Dispatch<ChatAction>
) {
  const hasStarted = useRef(false)
  const currentConversationId = useRef<string | undefined>()

  useEffect(() => {
    // Reset when conversation ID changes
    if (currentConversationId.current !== params.conversationId) {
      hasStarted.current = false
      currentConversationId.current = params.conversationId
      dispatch({ type: 'RESET_CONVERSATION' })
    }

    if (hasStarted.current) return
    hasStarted.current = true

    const initializeConversation = async () => {
      dispatch({ type: 'INIT_START' })
      
      try {
        const paramId = params.conversationId
        const state = navState

        console.log('Initializing conversation:', {
          paramId,
          state,
          hasStarted: hasStarted.current
        })

        // If we have a real conversation ID from params, load it
        if (paramId && paramId !== 'new') {
          try {
            console.log('Loading existing conversation:', paramId)
            const conversation = await chatService.getConversation(paramId)
            dispatch({ type: 'INIT_SUCCESS', payload: { conversation } })
            
            // Check if conversation needs initial message
            const nonSystemMessages = conversation.messages.filter(msg => msg.role !== 'system')
            console.log('Non-system messages count:', nonSystemMessages.length)
            
            if (nonSystemMessages.length > 1) {
              console.log('Conversation already initialized (has multiple messages)')
              return
            }
            
            // If conversation is empty and we have initialMessage, signal to send it
            if (nonSystemMessages.length === 0 && state?.initialMessage) {
              console.log('Conversation exists but is empty, needs initial message:', state.initialMessage)
              return
            }
            
            // If it has exactly 1 non-system message and we have initialMessage, send it
            if (nonSystemMessages.length === 1 && state?.initialMessage && state?.status !== 'initialized') {
              console.log('Sending initial message for existing conversation:', state.initialMessage)
              return
            }
            
            return
          } catch (error) {
            console.error('Failed to load conversation:', error)
            throw error
          }
        }

        // Handle initial message from navigation state
        if (state?.initialMessage) {
          console.log('Processing initial message:', state.initialMessage, 'Status:', state?.status)
          
          if (state?.status === 'initialized') {
            console.log('Conversation already initialized, displaying existing data')
            // Conversation is already initialized, just display it
            if (state?.conversation) {
              const conversation = state.conversation as Conversation
              dispatch({ type: 'INIT_SUCCESS', payload: { conversation } })
              return
            }
          } else {
            console.log('Not initialized - creating conversation')
            // Not initialized - need to create conversation
            const conversation = await chatService.createConversation(state.initialMessage)
            console.log('Conversation created:', conversation.conversation_id)
            dispatch({ type: 'INIT_SUCCESS', payload: { conversation } })
            return
          }
        } else {
          console.log('No initial message, creating empty conversation')
          // No initial message, create empty conversation
          const conversation = await chatService.createConversation('')
          dispatch({ type: 'INIT_SUCCESS', payload: { conversation } })
          return
        }
      } catch (error) {
        console.error('Failed to initialize conversation:', error)
        dispatch({ type: 'INIT_ERROR', error })
        return
      }
    }

    initializeConversation()
  }, [params.conversationId, navState, dispatch])

  // Return the bootstrap state for components to use
  return {
    isBootstrapping: !hasStarted.current
  }
} 